import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  Droplet,
  Info,
} from "lucide-react";

const ContainerWithMostWaterVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Standard Case",
      array: [1, 8, 6, 2, 5, 4, 8, 3, 7],
    },
    {
      id: 2,
      name: "Decreasing/Increasing",
      array: [4, 3, 2, 1, 4],
    },
    {
      id: 3,
      name: "Peak in Middle",
      array: [1, 2, 4, 3],
    },
    {
      id: 4,
      name: "Tall Edges",
      array: [10, 1, 1, 1, 1, 10],
    },
    {
      id: 5,
      name: "Two Elements",
      array: [2, 3],
    },
  ];

  // State setup
  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [heights, setHeights] = useState(defaultTestCases[0].array);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "function maxArea(height):", indent: 0 },
    { line: "left = 0, right = height.length - 1", indent: 1 },
    { line: "maxArea = 0", indent: 1 },
    { line: "while left < right:", indent: 1 },
    { line: "h = min(height[left], height[right])", indent: 2 },
    { line: "w = right - left", indent: 2 },
    { line: "currentArea = h * w", indent: 2 },
    { line: "maxArea = max(maxArea, currentArea)", indent: 2 },
    { line: "", indent: 0 },
    { line: "if height[left] < height[right]:", indent: 2 },
    { line: "left++", indent: 3 },
    { line: "else:", indent: 2 },
    { line: "right--", indent: 3 },
    { line: "return maxArea", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (heights) => {
    const allSteps = [];
    if (!heights || heights.length < 2) return [];
    let stepCount = 0;

    let left = 0;
    let right = heights.length - 1;
    let maxArea = 0;
    let maxAreaPointers = [-1, -1];

    const addStep = (desc, hl, details = {}) => {
      allSteps.push({
        step: stepCount++,
        description: desc,
        highlightLine: hl,
        left,
        right,
        maxArea,
        maxAreaPointers: [...maxAreaPointers],
        ...details,
      });
    };

    addStep("Start: Initialize pointers and maxArea.", 1);

    while (left < right) {
      addStep(`Checking pointers L=${left}, R=${right}.`, 3);
      const h = Math.min(heights[left], heights[right]);
      const w = right - left;
      const currentArea = h * w;
      addStep(
        `Area = min(${heights[left]}, ${heights[right]}) * (${right} - ${left}) = ${currentArea}.`,
        6,
        { currentArea }
      );

      if (currentArea > maxArea) {
        maxArea = currentArea;
        maxAreaPointers = [left, right];
        addStep(`New max area found: ${maxArea}.`, 7, { currentArea });
      }

      addStep(
        `Compare heights: height[${left}] (${heights[left]}) vs height[${right}] (${heights[right]}).`,
        9,
        { currentArea }
      );
      if (heights[left] < heights[right]) {
        left++;
        addStep(`Moved left pointer to ${left}.`, 10, { currentArea });
      } else {
        right--;
        addStep(`Moved right pointer to ${right}.`, 12, { currentArea });
      }
    }

    addStep(`Pointers have crossed. Final max area is ${maxArea}.`, 13, {
      isFinal: true,
    });
    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(heights);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
    setHighlightedLine(newSteps.length > 0 ? newSteps[0].highlightLine : -1);
  }, [heights]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(steps.length > 0 ? steps[0].highlightLine : -1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setHeights(testCases[index].array);
  };

  const addCustomTestCase = () => {
    try {
      const nums = JSON.parse(`[${customInput}]`);
      if (
        !Array.isArray(nums) ||
        nums.some((n) => isNaN(n) || n < 0) ||
        nums.length < 2
      ) {
        throw new Error(
          "Input must be an array of at least 2 non-negative numbers."
        );
      }
      const newTestCase = { id: Date.now(), name: `Custom`, array: nums };
      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
    } catch (error) {
      alert(`Invalid format: ${error.message}. Use comma-separated numbers.`);
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || {};
  const maxHeight = Math.max(...heights, 1);
  const containerHeight = 200;

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <Droplet className="text-blue-500" /> Container With Most Water
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                You are given an integer array `height` of length `n`. There are
                `n` vertical lines drawn such that the two endpoints of the
                `i-th` line are `(i, 0)` and `(i, height[i])`. Find two lines
                that together with the x-axis form a container, such that the
                container contains the most water.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      [{tc.array.join(", ")}]
                    </div>
                  </button>
                  {tc.id > 5 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Heights: 1,8,6,2,5,4,8,3,7"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            <div
              className="flex justify-center items-end border-b-2 border-gray-400 mt-8"
              style={{ height: containerHeight }}
            >
              {heights.map((h, idx) => {
                const heightPx = (h / maxHeight) * containerHeight;
                const isLeft = idx === currentStepData.left;
                const isRight = idx === currentStepData.right;
                const waterHeight = Math.min(
                  heights[currentStepData.left],
                  heights[currentStepData.right]
                );
                const waterHeightPx =
                  (waterHeight / maxHeight) * containerHeight;

                return (
                  <div
                    key={idx}
                    className="flex-1 flex flex-col items-center justify-end relative h-full"
                  >
                    <div
                      className={`w-full transition-colors duration-300 ${
                        isLeft || isRight ? "bg-blue-500" : "bg-gray-300"
                      }`}
                      style={{ height: `${heightPx}px` }}
                    >
                      <div className="text-center text-xs -mt-4 font-bold">
                        {h}
                      </div>
                    </div>
                    {isLeft && (
                      <div className="absolute -bottom-5 text-xs font-bold text-blue-600">
                        L
                      </div>
                    )}
                    {isRight && (
                      <div className="absolute -bottom-5 text-xs font-bold text-blue-600">
                        R
                      </div>
                    )}

                    {idx > currentStepData.left &&
                      idx < currentStepData.right && (
                        <div
                          className="absolute bottom-0 bg-blue-500/20"
                          style={{
                            height: `${waterHeightPx}px`,
                            width: "100%",
                          }}
                        ></div>
                      )}
                  </div>
                );
              })}
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4 text-center">
              <div className="p-2 rounded bg-yellow-100">
                <div className="text-xs text-yellow-800 font-medium">
                  Current Area
                </div>
                <div className="font-bold text-lg text-yellow-900">
                  {currentStepData.currentArea ?? "-"}
                </div>
              </div>
              <div className="p-2 rounded bg-green-100">
                <div className="text-xs text-green-800 font-medium">
                  Max Area Found
                </div>
                <div className="font-bold text-lg text-green-900">
                  {currentStepData.maxArea ?? "-"}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="1500"
                  step="100"
                  value={1700 - speed}
                  onChange={(e) => setSpeed(1700 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">Algorithm</h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              The problem is solved using the two-pointer technique. We start
              with one pointer at the beginning (`left`) and one at the end
              (`right`). We calculate the area and keep track of the maximum.
              The crucial step is deciding which pointer to move: we always move
              the pointer that points to the shorter of the two lines. This is
              because the area is limited by the shorter line, and moving the
              taller line inward will only decrease the width without any chance
              of increasing the height, thus guaranteeing a smaller area.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time:</span> O(N)
              </div>
              <div>
                <span className="font-medium">Space:</span> O(1)
              </div>
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The area is always limited by the shorter of the two vertical
                  lines.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  By moving the shorter pointer, we create a chance to find a
                  taller line, which is the only way to potentially increase the
                  area.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The two pointers will scan the entire array once, leading to a
                  linear time solution.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContainerWithMostWaterVisualizer;
